import React, { useEffect, useState } from "react";
import {
  Anchor,
  Button,
  Card,
  Group,
  Grid,
  Loader,
  Stack,
  Text,
  Title,
  Badge,
  SimpleGrid,
} from "@mantine/core";
import { Link } from "react-router-dom";
import {
  IconBuildingStore,
  IconListDetails,
  IconUpload,
  IconPlus,
  IconClock,
  IconTrendingUp,
  IconFileText,
} from "@tabler/icons-react";

import api from "../../services/api";

interface DashboardStats {
  totalRestaurants: number;
  totalMenus: number;
  totalMenuItems: number;
  menusToday: number;
  menusThisWeek: number;
}

interface RecentMenu {
  id: string;
  restaurantId: string;
  restaurant: {
    id: string;
    name: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Admin Dashboard - Overview page with statistics, quick actions, and recent activity
 * Uses lightweight endpoints for fast loading
 */
export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentMenus, setRecentMenus] = useState<RecentMenu[]>([]);
  const [newMenus, setNewMenus] = useState<RecentMenu[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all data in parallel using lightweight endpoints
        const [statsResponse, recentResponse, newResponse] = await Promise.all([
          api.get("/admin/dashboard/stats"),
          api.get("/admin/dashboard/recent-menus?type=updated&limit=5"),
          api.get("/admin/dashboard/recent-menus?type=created&limit=5"),
        ]);

        setStats(statsResponse.data.stats);
        setRecentMenus(recentResponse.data.menus || []);
        setNewMenus(newResponse.data.menus || []);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (isLoading || !stats) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={2}>Dashboard</Title>

      {/* Statistics Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <Card withBorder p="md" radius="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Restaurants
              </Text>
              <Text size="xl" fw={700}>
                {stats.totalRestaurants}
              </Text>
            </Stack>
            <IconBuildingStore size={32} color="var(--mantine-color-green-6)" />
          </Group>
        </Card>

        <Card withBorder p="md" radius="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Total Menus
              </Text>
              <Text size="xl" fw={700}>
                {stats.totalMenus}
              </Text>
            </Stack>
            <IconListDetails size={32} color="var(--mantine-color-green-6)" />
          </Group>
        </Card>

        <Card withBorder p="md" radius="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Menu Items
              </Text>
              <Text size="xl" fw={700}>
                {stats.totalMenuItems}
              </Text>
            </Stack>
            <IconFileText size={32} color="var(--mantine-color-orange-6)" />
          </Group>
        </Card>

        <Card withBorder p="md" radius="md">
          <Group justify="space-between">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                This Week
              </Text>
              <Text size="xl" fw={700}>
                {stats.menusThisWeek}
              </Text>
              <Text size="xs" c="dimmed">
                {stats.menusToday} today
              </Text>
            </Stack>
            <IconTrendingUp size={32} color="var(--mantine-color-purple-6)" />
          </Group>
        </Card>
      </SimpleGrid>

      {/* Quick Actions */}
      <Card withBorder p="md">
        <Title order={3} mb="md">
          Quick Actions
        </Title>
        <Group gap="md">
          <Button
            component={Link}
            to="/admin/restaurants/create"
            leftSection={<IconPlus size={16} />}
            variant="light"
          >
            Create Restaurant
          </Button>
          <Button
            component={Link}
            to="/admin/upload"
            leftSection={<IconUpload size={16} />}
            variant="light"
          >
            Upload Menu
          </Button>
          <Button
            component={Link}
            to="/admin/upload/fast"
            leftSection={<IconUpload size={16} />}
            variant="light"
          >
            Fast Upload
          </Button>
          <Button
            component={Link}
            to="/admin/upload/csv"
            leftSection={<IconUpload size={16} />}
            variant="light"
          >
            CSV Upload
          </Button>
        </Group>
      </Card>

      <Grid>
        {/* Navigation Cards */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            withBorder
            p="lg"
            radius="md"
            component={Link}
            to="/admin/restaurants"
            style={{ textDecoration: "none", cursor: "pointer" }}
          >
            <Stack gap="md">
              <Group>
                <IconBuildingStore size={32} color="var(--mantine-color-green-6)" />
                <Title order={3}>Restaurants</Title>
              </Group>
              <Text c="dimmed" size="sm">
                Manage restaurants, view details, and edit restaurant information.
              </Text>
              <Group>
                <Badge variant="light">
                  {stats.totalRestaurants} total
                </Badge>
                <Text size="sm" c="dimmed">
                  View all →
                </Text>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card
            withBorder
            p="lg"
            radius="md"
            component={Link}
            to="/admin/menus"
            style={{ textDecoration: "none", cursor: "pointer" }}
          >
            <Stack gap="md">
              <Group>
                <IconListDetails size={32} color="var(--mantine-color-green-6)" />
                <Title order={3}>Menus</Title>
              </Group>
              <Text c="dimmed" size="sm">
                View and edit menus, manage menu pages and items.
              </Text>
              <Group>
                <Badge variant="light">
                  {stats.totalMenus} total
                </Badge>
                <Text size="sm" c="dimmed">
                  View all →
                </Text>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <Grid>
        {/* Recently Updated Menus */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder p="md">
            <Group justify="space-between" mb="md">
              <Title order={4}>Recently Updated</Title>
              <IconClock size={20} />
            </Group>
            {recentMenus.length > 0 ? (
              <Stack gap="xs">
                {recentMenus.map((menu) => (
                  <Group key={menu.id} justify="space-between" wrap="nowrap">
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} truncate>
                        {menu.restaurant?.name ?? menu.restaurantId}
                      </Text>
                      <Text size="xs" c="dimmed" truncate style={{ fontFamily: "monospace" }}>
                        {menu.id.slice(0, 8)}...
                      </Text>
                    </Stack>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="xs" c="dimmed">
                        {menu.updatedAt
                          ? new Date(menu.updatedAt).toLocaleDateString()
                          : "—"}
                      </Text>
                      <Anchor
                        component={Link}
                        to={`/admin/menus/edit/${menu.id}`}
                        size="xs"
                      >
                        Edit
                      </Anchor>
                    </Group>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No menus found
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Anchor component={Link} to="/admin/menus" size="sm">
                View all menus →
              </Anchor>
            </Group>
          </Card>
        </Grid.Col>

        {/* Recently Created Menus */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Card withBorder p="md">
            <Group justify="space-between" mb="md">
              <Title order={4}>Recently Created</Title>
              <IconPlus size={20} />
            </Group>
            {newMenus.length > 0 ? (
              <Stack gap="xs">
                {newMenus.map((menu) => (
                  <Group key={menu.id} justify="space-between" wrap="nowrap">
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} truncate>
                        {menu.restaurant?.name ?? menu.restaurantId}
                      </Text>
                      <Text size="xs" c="dimmed" truncate style={{ fontFamily: "monospace" }}>
                        {menu.id.slice(0, 8)}...
                      </Text>
                    </Stack>
                    <Group gap="xs" wrap="nowrap">
                      <Text size="xs" c="dimmed">
                        {menu.createdAt
                          ? new Date(menu.createdAt).toLocaleDateString()
                          : "—"}
                      </Text>
                      <Anchor
                        component={Link}
                        to={`/admin/menus/edit/${menu.id}`}
                        size="xs"
                      >
                        Edit
                      </Anchor>
                    </Group>
                  </Group>
                ))}
              </Stack>
            ) : (
              <Text size="sm" c="dimmed">
                No menus found
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Anchor component={Link} to="/admin/menus" size="sm">
                View all menus →
              </Anchor>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}






